import { Typography, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';

import { Props as VMProps } from './vm';
import { generateClasses } from '../../utils/helper';

export const variants = ['future', 'near', 'live', 'past', 'unpublished'] as const;

export type DateWidgetVariant = (typeof variants)[number];

export const dateWidgetClasses = {
  ...generateClasses('DateWidget', [...variants, 'root', 'month', 'date', 'day']),
};

export interface Props extends VMProps {
  variant?: DateWidgetVariant;
  className?: string;
  classes?: Partial<typeof dateWidgetClasses>;
}

export const Root = styled('div')(({ theme }) => ({
  borderRadius: 3,
  width: theme.spacing(10),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'hidden',
  [`&.${dateWidgetClasses.future}`]: {
    [`& .${dateWidgetClasses.month}`]: {
      backgroundColor: theme.palette.primary[100],
      color: theme.palette.primary[700],
      border: `1px solid ${theme.palette.grey[200]}`,
      borderBottom: 'none',
    },
    [`& .${dateWidgetClasses.day}`]: {
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.grey[500],
    },
  },
  [`&.${dateWidgetClasses.near}`]: {
    [`& .${dateWidgetClasses.month}`]: {
      backgroundColor: theme.palette.error[400],
      color: theme.palette.background.paper,
    },
    [`& .${dateWidgetClasses.day}`]: {
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.grey[500],
    },
  },
  [`&.${dateWidgetClasses.live}`]: {
    [`& .${dateWidgetClasses.month}`]: {
      backgroundColor: theme.palette.success[600],
      color: theme.palette.background.paper,
    },
    [`& .${dateWidgetClasses.day}`]: {
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.grey[500],
    },
  },
  [`&.${dateWidgetClasses.past}`]: {
    [`& .${dateWidgetClasses.month}`]: {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.grey[400],
      border: `1px solid ${theme.palette.grey[200]}`,
      borderBottom: 'none',
    },
    [`& .${dateWidgetClasses.day}`]: {
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.grey[400],
    },
  },
  [`&.${dateWidgetClasses.unpublished}`]: {
    [`& .${dateWidgetClasses.month}`]: {
      backgroundColor: theme.palette.error[500],
      color: theme.palette.background.paper,
    },
    [`& .${dateWidgetClasses.day}`]: {
      backgroundColor: theme.palette.error[50],
      color: theme.palette.error[900],
    },
  },
}));

export const Month = styled((props: TypographyProps) => <Typography variant="titleBold" {...props} />)(
  ({ theme }) => ({
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
    padding: theme.spacing(1, 0.375),
    width: '100%',
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(9),
  })
);

export const Date = styled((props: TypographyProps) => <Typography variant="h5Bold" {...props} />)(
  ({ theme }) => ({
    borderLeft: `1px solid ${theme.palette.grey[200]}`,
    borderRight: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.grey[900],
    padding: theme.spacing(0.75),
    width: '100%',
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(15),
  })
);

export const Day = styled((props: TypographyProps) => <Typography variant="titleRegular" {...props} />)(
  ({ theme }) => ({
    border: `1px solid ${theme.palette.grey[200]}`,
    borderTop: 'none',
    borderBottomLeftRadius: 3,
    borderBottomRightRadius: 3,
    padding: theme.spacing(1, 0.375),
    width: '100%',
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(9),
  })
);
